import type { ColumnDef } from '@react-apps/design-system';
import type { TransactionListItem } from '../../domain/entities/sales.entity';
import { EntityLink } from '@/shared/components/entity-link';
import {
  TransactionStatusBadge,
  PaymentMethodBadge,
  TransactionStatusIcon,
  formatCurrency,
  formatDateTimeWithSeconds,
  tenantLabel,
} from './display';
import { SALES_SORT_OPTIONS, type SalesSortField } from './sales-filters';

/**
 * As colunas da listagem de `/sales` — extraídas para uso também na aba
 * "transactions" de `/offers/:id` (mesma forma de linha, mesmo DTO).
 *
 * A coluna de data é a do campo da ordem (`sortBy`): ordenar por "Registrada
 * em" troca a "Data da cobrança" por ela, e a tela nunca mostra uma ordem por
 * um campo que não aparece. Sem `sortBy` (aba da oferta, que não ordena) fica a
 * data da cobrança, sem cabeçalho clicável — seria promessa sem efeito.
 */
export function buildSalesTransactionColumns({
  sortBy,
}: { sortBy?: SalesSortField } = {}): ColumnDef<TransactionListItem>[] {
  const dateField = sortBy ?? 'occurredAt';
  const dateLabel =
    SALES_SORT_OPTIONS.find((option) => option.value === dateField)?.label ??
    'Data da cobrança';

  return [
    {
      key: 'product',
      header: 'Produto',
      width: '250px',
      render: (row) => (
        <div className="flex items-center gap-2">
          <span className="shrink-0">
            <TransactionStatusIcon status={row.status} />
          </span>
          <span className="text-(--color-text-primary) text-xs">
            {row.productName ?? '---'}
          </span>
        </div>
      ),
    },
    {
      key: 'contact',
      header: 'Contato',
      // O contato é um usuário com tela própria — o nome leva até ela.
      render: (row) => (
        <span className="text-(--color-text-primary)">
          <EntityLink kind="user" id={row.userId} fallback="---">
            {row.contactName ?? '---'}
          </EntityLink>
        </span>
      ),
    },
    {
      key: 'businessUnit',
      header: 'BU',
      render: (row) => (
        <span className="text-(--color-text-secondary)">
          {tenantLabel(row.tenant)}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (row) => <TransactionStatusBadge status={row.status} />,
    },
    {
      key: 'amount',
      header: 'Valor',
      render: (row) => (
        <span className="text-(--color-text-primary) font-medium">
          {formatCurrency(row.amount)}
        </span>
      ),
    },
    {
      key: 'paymentMethod',
      header: 'Método',
      render: (row) =>
        row.paymentMethod ? (
          <PaymentMethodBadge method={row.paymentMethod} />
        ) : (
          <span className="text-(--color-text-secondary)">—</span>
        ),
    },
    {
      key: dateField,
      header: dateLabel,
      sortable: sortBy !== undefined,
      render: (row) => (
        <span className="text-(--color-text-secondary)">
          {formatDateTimeWithSeconds(row[dateField])}
        </span>
      ),
    },
  ];
}
